import React, { useEffect, useRef, useState } from 'react';
import { Checkbox, Modal, TextField } from '@gerdau/hefesto';
import { KeyValueItem } from '@gerdau/hefesto-mais-web';
import { Button } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../../../store';
import {
  cleanErrors,
  createCustomerUser
} from '../../../../../store/ducks/access-management/actions';
import { IAccessManagementState } from '../../../../../store/ducks/access-management/types';
import { ICustomer } from '../../../../../store/ducks/customers/types';
import { sendTagging } from '../../../../../store/ducks/tagging/actions';
import { getTaggingInformationScreen } from '../../../../../utils/Tagging';
import { adobeCommerceSanitize } from '../../../../../utils/sanitizeUtils';
import {
  AccessItems,
  Form,
  PermissionItem,
  PermissionsContainer,
  PermissionsItems,
  PermissionsModalContentBlock,
  Container
} from './styles';

interface IAddUserModalProps {
  customer: ICustomer;
  onClose(): void;
  accessManagementState: IAccessManagementState;
  setCanShowAddUserMessage(canShowAddUserMessage: boolean): void;
}

enum EAccessPermissionTypes {
  ADMIN = 'adminCustomers',
  FABRICATION = 'fabricationViewCustomers'
}

enum EVisualizationPermissionType {
  FINANCIAL_VIEW = 'financialViewPermission',
  ORDERS_INPUT = 'ordersInputPermission',
  ORDERS_VIEW = 'ordersViewPermission',
  ORDERS_VIEW_PRICE = 'ordersViewPrice'
}

type TPermissionTypes = EAccessPermissionTypes | EVisualizationPermissionType;

interface IPermissionOption {
  id: TPermissionTypes;
  description: string | React.ReactNode;
  disabledOptionIfVisualizationIncludes?: EVisualizationPermissionType;
}

const accessPermissionsOptions: IPermissionOption[] = [
  {
    id: EAccessPermissionTypes.ADMIN,
    description: (
      <KeyValueItem
        title={'Administrador da conta'}
        helperText={
          'Administradores podem cadastrar novos usuários e possuem todas as permissões'
        }
        children={<></>}
      ></KeyValueItem>
    )
  },
  {
    id: EAccessPermissionTypes.FABRICATION,
    description: (
      <KeyValueItem
        title={' Visualizar corte e dobra'}
        helperText={
          'Permite que o usuário navegue pelas opções de Corte e Dobra do eGerdau.'
        }
        children={<></>}
      ></KeyValueItem>
    )
  }
];

const visualizationPermissionsOptions: IPermissionOption[] = [
  {
    id: EVisualizationPermissionType.ORDERS_VIEW,
    description: 'Visualizar pedidos'
  },
  {
    id: EVisualizationPermissionType.ORDERS_VIEW_PRICE,
    description: 'Visualizar preços da ordem',
    disabledOptionIfVisualizationIncludes:
      EVisualizationPermissionType.FINANCIAL_VIEW
  },
  {
    id: EVisualizationPermissionType.FINANCIAL_VIEW,
    description: 'Visualizar finanças'
  },
  { id: EVisualizationPermissionType.ORDERS_INPUT, description: 'Comprar' }
];

const AddUserModal: React.FC<IAddUserModalProps> = ({
  customer,
  onClose,
  accessManagementState,
  setCanShowAddUserMessage
}) => {
  const dispatch = useDispatch();
  const buttonRef = useRef<HTMLElement>(null);

  const userState = useSelector((state: ApplicationState) => state.user);
  const canShowFabricationOption =
    (userState.roles.fabricationViewCustomers &&
      userState.roles.fabricationViewCustomers.includes(customer.customerId)) ||
    userState.isManager;

  const [form] = Form.useForm();

  const [accessPermissions, setAccessPermissions] = useState<
    EAccessPermissionTypes[]
  >([]);
  const [visualizationPermissions, setVisualizationPermissions] = useState<
    EVisualizationPermissionType[]
  >([]);

  const { screenName = '', itemDescription = '' } =
    getTaggingInformationScreen('modal-add-user');

  const inputPattern = /^[A-Za-zÀ-ÖØ-öø-ÿ0-9,\-_.’'`&\s]{1,255}$/;

  useEffect(() => {
    dispatch(
      sendTagging({
        event: 'page_view',
        screenName
      })
    );
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const saveUser = (fieldsValue: any) => {
    const { name, email, lastName } = fieldsValue;

    const isPermissionSelected = (
      permission: TPermissionTypes,
      permissionsSelected: TPermissionTypes[]
    ): boolean => {
      return permissionsSelected.includes(permission);
    };

    const completeName = `
      ${adobeCommerceSanitize(name)} ${adobeCommerceSanitize(lastName)}
    `;

    dispatch(
      createCustomerUser(
        customer.customerId,
        completeName,
        email,
        isPermissionSelected(EAccessPermissionTypes.ADMIN, accessPermissions),
        isPermissionSelected(
          EAccessPermissionTypes.FABRICATION,
          accessPermissions
        ),

        isPermissionSelected(
          EVisualizationPermissionType.ORDERS_VIEW,
          visualizationPermissions
        ),
        isPermissionSelected(
          EVisualizationPermissionType.ORDERS_VIEW_PRICE,
          visualizationPermissions
        ),
        isPermissionSelected(
          EVisualizationPermissionType.FINANCIAL_VIEW,
          visualizationPermissions
        ),
        isPermissionSelected(
          EVisualizationPermissionType.ORDERS_INPUT,
          visualizationPermissions
        ),

        false // coreteam
      )
    );

    setCanShowAddUserMessage(true);
  };

  const addVisualizationPermissionsIfDoesntYet = (
    permissions: EVisualizationPermissionType[]
  ) => {
    const newVisualizationPermissions = visualizationPermissions;

    permissions.forEach((permission) => {
      if (!newVisualizationPermissions.includes(permission)) {
        newVisualizationPermissions.push(permission);
      }
    });

    setVisualizationPermissions([...newVisualizationPermissions]);
  };

  const onChangeAccessPermissions = (
    checkedValue: EAccessPermissionTypes[],
    added: boolean
  ) => {
    let newAccessPermissions = checkedValue;

    newAccessPermissions = added
      ? accessPermissions.concat(newAccessPermissions)
      : accessPermissions.filter(
          (permission) => permission !== newAccessPermissions[0]
        );

    const addAllLongSteelPermissions = () => {
      addVisualizationPermissionsIfDoesntYet([
        EVisualizationPermissionType.ORDERS_VIEW,
        EVisualizationPermissionType.ORDERS_VIEW_PRICE,
        EVisualizationPermissionType.FINANCIAL_VIEW,
        EVisualizationPermissionType.ORDERS_INPUT
      ]);
    };

    if (newAccessPermissions.includes(EAccessPermissionTypes.ADMIN)) {
      addAllLongSteelPermissions();
    }

    setAccessPermissions(newAccessPermissions);
  };

  const onChangeVisualizationPermissions = (
    checkedValue: EVisualizationPermissionType[],
    added: boolean
  ) => {
    let newVisualizationPermissions = checkedValue;

    newVisualizationPermissions = added
      ? newVisualizationPermissions.concat(visualizationPermissions)
      : visualizationPermissions.filter(
          (permisson: EVisualizationPermissionType) =>
            permisson !== newVisualizationPermissions[0]
        );

    if (
      newVisualizationPermissions.includes(
        EVisualizationPermissionType.FINANCIAL_VIEW
      ) &&
      !newVisualizationPermissions.includes(
        EVisualizationPermissionType.ORDERS_VIEW_PRICE
      )
    ) {
      newVisualizationPermissions.push(
        EVisualizationPermissionType.ORDERS_VIEW_PRICE
      );
    }

    setVisualizationPermissions(newVisualizationPermissions);
  };

  return (
    <Container>
      <Modal
        onClose={() => null}
        modalSize="sm"
        title={'Adicionar usuário'}
        buttons={{
          left: {
            children: 'Cancelar',
            name: 'Cancelar',
            onClick: () => {
              dispatch(
                sendTagging({
                  event: 'click',
                  itemName: 'button:cancelar',
                  itemDescription: `${itemDescription}_cancelar`,
                  screenName
                })
              );
              dispatch(cleanErrors());
              onClose();
            },
            type: 'button',
            disabled: accessManagementState.loadingCreateCustomerUser
          },
          right: {
            children: 'Adicionar usuário',
            name: 'Adicionar usuário',
            onClick: () => {
              dispatch(
                sendTagging({
                  event: 'click',
                  itemName: 'button:adicionar_usuario',
                  itemDescription: `${itemDescription}_adicionarUsuario`,
                  screenName
                })
              );
              const saveButton = buttonRef.current;
              saveButton && saveButton.click();
            },
            type: 'button',
            disabled: accessManagementState.loadingCreateCustomerUser
          }
        }}
      >
        <span className="subtitle">
          O novo usuário receberá os dados de acesso através do email cadastrado
          neste formulário.
        </span>

        <Form
          id="create-user-form"
          form={form}
          onFinish={saveUser}
          layout="vertical"
        >
          <PermissionsModalContentBlock>
            <Form.Item
              name="name"
              rules={[
                {
                  type: 'string'
                },
                {
                  required: true,
                  message: 'Campo obrigatório'
                },
                {
                  pattern: inputPattern,
                  message: 'Não use caracteres especiais'
                }
              ]}
            >
              <TextField
                label={'Nome'}
                name={'name'}
                placeholder="Digite o nome"
                disabled={accessManagementState.loadingCreateCustomerUser}
              />
            </Form.Item>

            <Form.Item
              name="lastName"
              rules={[
                {
                  type: 'string'
                },
                {
                  required: true,
                  message: 'Campo obrigatório'
                },
                {
                  pattern: inputPattern,
                  message: 'Não use caracteres especiais'
                }
              ]}
            >
              <TextField
                label={'Sobrenome'}
                name={'lastName'}
                placeholder="Digite o sobrenome"
                disabled={accessManagementState.loadingCreateCustomerUser}
              />
            </Form.Item>
          </PermissionsModalContentBlock>

          <Form.Item
            name="email"
            rules={[
              {
                type: 'email',
                message: 'O valor digitado não é um e-mail válido'
              },
              {
                required: true,
                message: 'Campo obrigatório'
              }
            ]}
          >
            <TextField
              label={'Email'}
              name={'email'}
              disabled={accessManagementState.loadingCreateCustomerUser}
              placeholder="exemplo@email.com"
            />
          </Form.Item>

          <PermissionsContainer>
            <KeyValueItem title="Acessos">
              <></>
            </KeyValueItem>

            <AccessItems>
              {accessPermissionsOptions.map((permission) => {
                if (
                  !canShowFabricationOption &&
                  permission.id === EAccessPermissionTypes.FABRICATION
                ) {
                  return <React.Fragment key={permission.id} />;
                }
                return (
                  <PermissionItem key={permission.id}>
                    <Checkbox
                      name={permission.id}
                      key={permission.id}
                      disabled={accessManagementState.loadingCreateCustomerUser}
                      onChange={(event) => {
                        onChangeAccessPermissions(
                          [event.target.name] as EAccessPermissionTypes[],
                          event.target.checked
                        );
                      }}
                    />
                    {permission.description}
                  </PermissionItem>
                );
              })}
            </AccessItems>
          </PermissionsContainer>

          <PermissionsContainer>
            <KeyValueItem
              helperText={'Selecione o que o usuário terá acesso no eGerdau'}
              title="Permissões"
            >
              <></>
            </KeyValueItem>
            <PermissionsItems>
              {visualizationPermissionsOptions.map((permission) => {
                const permissionEnabled = visualizationPermissions.find(
                  (visualizationPermission) =>
                    visualizationPermission === permission.id
                );

                return (
                  <PermissionItem key={permission.id}>
                    <Checkbox
                      name={permission.id}
                      key={permission.id}
                      onChange={(event) => {
                        onChangeVisualizationPermissions(
                          [event.target.name] as EVisualizationPermissionType[],
                          event.target.checked
                        );
                      }}
                      checked={
                        accessPermissions.includes(
                          EAccessPermissionTypes.ADMIN
                        ) || !!permissionEnabled
                      }
                      readOnly={
                        accessPermissions.includes(
                          EAccessPermissionTypes.ADMIN
                        ) ||
                        (permission.disabledOptionIfVisualizationIncludes &&
                          visualizationPermissions.includes(
                            permission.disabledOptionIfVisualizationIncludes
                          )) ||
                        accessManagementState.loadingCreateCustomerUser
                      }
                    />
                    {permission.description}
                  </PermissionItem>
                );
              })}
            </PermissionsItems>
          </PermissionsContainer>

          {visualizationPermissions.length === 0 && (
            <Form.Item
              name="access"
              className="fake-form-item"
              rules={[
                {
                  required: true,
                  message: 'É obrigatório selecionar pelo menos uma permissão'
                }
              ]}
            >
              <input style={{ display: 'none' }} />
            </Form.Item>
          )}
        </Form>

        <Button
          type="primary"
          ref={buttonRef}
          name="saveUserButton"
          style={{ display: 'none' }}
          form={'create-user-form'}
          key="submit"
          htmlType="submit"
          disabled={accessManagementState.loadingCreateCustomerUser}
          loading={accessManagementState.loadingCreateCustomerUser}
        >
          Salvar
        </Button>
      </Modal>
    </Container>
  );
};

export default AddUserModal;
